import { ChevronRightIcon } from '@primer/octicons-react';
import { NavList, Tooltip } from '@primer/react';
import styled from 'styled-components';
import { Badge } from '@upright/ui/common/generic/components/badge';
import { LinkWithSearch } from '@upright/ui/common/generic/components/navigation/link-with-search';
import { breakpoints } from '@upright/ui/common/generic/global/breakpoints';
import { LockClosed } from '@upright/ui/icons';
import { navActiveStyles, navRowBox } from './nav-active-styles';

/**
 * Shared navigation components for DMA, DD, and Investor tools
 *
 * These components provide a consistent navigation design across all tools.
 * They are based on Primer's NavList component with custom styling. This is the one copy:
 * the DMA dashboard and settings rails, the investor home rail, the unified shell's rail and
 * the prototypes all mount these (PLT-6226 folded the DMA dashboard's own copy in here, with
 * that copy's treatment as the spec — `design/40-shell-and-pages.md`, "Sidebar").
 *
 * @example Basic usage (Investor tool)
 * ```tsx
 * import {
 *   StyledNavList,
 *   TopLevelItemWrap,
 *   NavSectionTitle,
 *   SubNavItem,
 *   getAriaCurrent,
 * } from '@upright/ui/views/shared/navigation/styled-nav-components';
 *
 * export default function MainNavigation() {
 *   const location = useLocation();
 *
 *   return (
 *     <StyledNavList>
 *       <TopLevelItemWrap $isFirstItem>
 *         <NavList.Item to="/" as={LinkWithSearch} aria-current={...}>
 *           <NavList.LeadingVisual>
 *             <Icon width={20} height={20} />
 *           </NavList.LeadingVisual>
 *           Label
 *         </NavList.Item>
 *       </TopLevelItemWrap>
 *
 * Do not nudge the leading visual with a `marginLeft` of its own — this example used to, because a
 * top-level row kept Primer's narrower padding. The wrapper now gives every nav-row anchor the
 * shared `navRowBox`, so an inline nudge on top of it doubles the inset.
 *
 *       <TopLevelItemWrap $isSection>
 *         <NavList.Item defaultOpen={true}>
 *           <NavSectionTitle>Section Title</NavSectionTitle>
 *           <NavList.SubNav aria-expanded={true}>
 *             <SubNavItem to="/path" ariaCurrent={getAriaCurrent('/path', location.pathname)} icon={Icon}>
 *               Sub Item
 *             </SubNavItem>
 *           </NavList.SubNav>
 *         </NavList.Item>
 *       </TopLevelItemWrap>
 *     </StyledNavList>
 *   );
 * }
 * ```
 *
 * @see src/ui/views/home-views/organizations-assets-view/main-navigation.tsx - Investor tool example
 * @see src/ui/views/dma-v2-view/dashboard/main-navigation.tsx - DMA tool example
 */

// ============================================================================
// Main Navigation Container
// ============================================================================

export const StyledNavList: typeof NavList = styled(NavList)`
  font-weight: var(--font-weight__medium);
  padding-left: var(--space__lg);
  padding-right: var(--space__lg);
  padding-top: var(--space__lg);
  padding-bottom: var(--space__xl);

  /* Primer's inset ActionList variant adds 8px margin-inline to every list item.
     Override so nav items align with the container's left edge. */
  li.prc-ActionList-ActionListItem-uq6I7 {
    margin-inline: 0;
  }

  /* The same variant also pads the list 8px top and bottom, and that lands on top of this nav's
     own padding: the inset is declared lg on three sides here, and the context selector rendered
     24px below the header against 16px at its left and right. The list owns none of the rail's
     inset, so the four values above are the four the rail actually has (PLT-6818). The ul is
     NavList's only child element, matched by tag because Primer's own class carries a build
     hash. */
  > ul {
    padding-block: 0;
  }

  /* Hover owns the fill and only keyboard focus owns a stroke. The rule that strips Primer's
     pointer-state outline and shadow from every list row, this rail included, lives in
     theme.css so that menus get it too. */

  /* Active sub-nav link, plus the Primer [data-active] override that goes with it. Shared with the
     DMA dashboard's nav, which paints the same row — see nav-active-styles. */
  ${navActiveStyles}
` as typeof NavList;

// ============================================================================
// Top Level Item Wrapper
// ============================================================================

export const TopLevelItemWrap = styled.div<{
  $isFirstItem?: boolean;
  $isSection?: boolean;
}>`
  /* The containing block for a section's trailing action (NavSectionAction below), which has to
     sit outside the <li> Primer renders. */
  position: relative;

  button {
    background-color: transparent;
  }

  [aria-expanded='false'] {
    :after {
      display: none;
    }
  }

  li {
    width: 100%;
    background-color: transparent;
    margin-left: 0;
    margin-right: 0;
    /* At rest as well as on hover. The row is transparent at rest so the radius shows only when
       something paints on this box, and two things do: Primer's hover fill, and the focus ring
       that Primer aims at this <li> rather than at the <a> inside it. Left at Primer's md, a
       keyboard-focused row rings as a 6px-cornered rectangle around a pill (PLT-6696). */
    border-radius: var(--border-radius__full);

    span {
      font-weight: var(--font-weight__medium);
      flex-grow: 0;
    }

    /* Apply flex-grow: 1 to disabled items' label spans */

    a[data-disabled='true'] [data-component='ActionList.Item--DividerContainer'] [id*='--label'] {
      flex-grow: 1;
    }

    :after {
      display: none;
    }

    /* A section title's hover is painted by Primer on the title's own <button>, not on the <li> the
       rule above rounds — Primer skips the <li> of an item that has sub-items — so without this the
       title hovers as a 6px-cornered band between pill-shaped rows. It takes the whole row box and
       not just the radius: Primer's condensed sm inline padding leaves a 32px row's 16px end curve
       running into the first letter of the title and into the glyph at the other end, which is the
       same crowding lg was introduced to fix on the rows below it (PLT-6696).
       design/40-shell-and-pages.md "Sidebar". */
    > button {
      ${navRowBox}
    }

    /* Primer gives any item that has a SubNav a trailing chevron of its own. A section header
       carries a *leading* one instead (NavSectionTitle), so the trailing slot is empty by design.
       Matched on a class substring rather than the full name the rules above use, because this is
       the only Primer class here whose hash is not already pinned by a shipped selector — and one
       release bumping it would put two chevrons on every section header rather than fail loudly. */
    > button [class*='TrailingVisual'] {
      display: none;
    }

    /* Top-level rows that are links rather than section titles take the same box as the sub-rows,
       so the pill curves clear of the leading icon (PLT-6076). No such row ships in this nav today
       — every icon row goes through SubNavItem — but the rule is what lets one be added without
       hand-nudging its leading visual. */
    a {
      ${navRowBox}
    }
  }

  /* Spacing logic based on item type: a caller declares the row's type — first row (flush with the
     rail's top padding), section (a titled group, a full gap above it), or a plain row that is not
     the first one (a hair's gap).

     Test the props against \`true\`/\`false\`, never for falsiness: an absent prop is not the same as
     \`false\`. The DMA rails pass no props at all, and a \`!props.$isFirstItem\` test counted that as
     "not the first row", giving their first wrapper a 4px margin their own wrapper never had — which
     dropped the entire DMA rail 4px below where master paints it (PLT-6226). */
  ${props => props.$isFirstItem === true && `margin-top: 0;`}
  ${props => props.$isFirstItem === false && props.$isSection !== true && `margin-top: var(--space__xs);`}
  ${props => props.$isSection === true && `margin-top: var(--space__lg);`}

  /* Fallback for rails that declare no row types (the DMA dashboard and settings navs): space
     adjacent wrappers, which is what the DMA's own wrapper did before it was folded in here. */
  ${props =>
    props.$isFirstItem === undefined &&
    props.$isSection === undefined &&
    `
    & + & {
      margin-top: var(--space__lg);
    }
  `}
`;

// ============================================================================
// Section Title
// ============================================================================

/* The uppercase title of a collapsible section, inside Primer's `NavList.Item` so the whole row
   is the section's toggle button (design/40-shell-and-pages.md "Sidebar"). Not exported: a rail
   mounts `NavSectionTitle` below, which is this plus the twistie that belongs beside it. */
const TopLevelItemTitle = styled.div`
  display: flex;
  align-items: center;
  /* Packed at the leading edge, never spread: the twistie belongs to the words, not to the row's
     far corner. The label cell Primer gives this div is as wide as the rail. */
  justify-content: flex-start;
  gap: var(--space__sm);
  font-weight: var(--font-weight__bold);
  font-size: var(--font-size__sm);
  margin: 0;
  letter-spacing: 0.05em;
  color: var(--platform-gray-800);
  text-transform: uppercase;
`;

/**
 * The section's disclosure chevron: tucked against the end of the title, and rotating a right
 * angle rather than flipping.
 *
 * Closed it points right, open it points down, which is the Finder grammar Janne asked for
 * (PLT-6696). It sits beside the words rather than at the row's far edge because the title is
 * what it discloses, and a glyph 200px away across empty rail reads as a second control. Primer's
 * own twistie is a `ChevronDownIcon` in the row's trailing visual, which is exactly that; the
 * wrapper above hides it.
 *
 * The direction is read off the toggle button's `aria-expanded` in CSS, the way Primer reads its
 * own: `NavList.Item` owns the open state and exposes no controlled `open` prop, so React never
 * holds it.
 */
const NavSectionTwistie = styled.span`
  display: inline-flex;
  align-items: center;
  flex: none;
  color: var(--color__gray-600);

  svg {
    transition: transform 150ms cubic-bezier(0.2, 0, 0.38, 0.9);
  }

  /* The 1px lift is a correction, not a taste call. Octicons' chevron-right is drawn vertically
     centred in its 16px box but about 1px right of centre horizontally, so turning it a right
     angle turns that offset into a vertical one and the open state alone hangs ~1px low against
     the title's caps. Correcting it here rather than in the closed state, which is already
     centred (design/10-foundations.md "Optical spacing": the visual result is what has to land,
     and the CSS is whatever produces it). */
  [aria-expanded='true'] & svg {
    transform: translateY(-1px) rotate(90deg);
  }
`;

/**
 * A collapsible section's header content: the title, then the twistie beside it.
 *
 * Rendered as the children of a `NavList.Item` that also carries a `NavList.SubNav`, which is what
 * makes the whole row the section's toggle button rather than a heading with a control beside it.
 * The pair is packed at the row's leading edge, so the title starts in the icon column of the rows
 * below it and the glyph follows the words however short or long they are.
 */
export const NavSectionTitle = ({ children }: { children: string }) => (
  <TopLevelItemTitle>
    {children}
    <NavSectionTwistie aria-hidden="true">
      <ChevronRightIcon size={16} />
    </NavSectionTwistie>
  </TopLevelItemTitle>
);

// ============================================================================
// Section Trailing Action
// ============================================================================

/**
 * A section header's one trailing action, pinned to the far end of the header row.
 *
 * **It is a sibling of the header, not a child of it.** The header row *is* the section's toggle
 * `<button>` (see `NavSectionTitle`), and a link or a button inside a button is invalid markup
 * that the browser reparses, so the control cannot go in there. Primer's own
 * `NavList.TrailingAction` is no help either: `NavList.Item` filters the trailing action out of
 * its children whenever the item carries a `SubNav`, which every section header does. So the
 * caller renders this next to the `NavList.Item`, inside `TopLevelItemWrap`, and it is positioned
 * over the header row from there.
 *
 * **The offsets are the row's own box, not a measured height.** `TopLevelItemWrap` wraps the
 * whole group, so its height includes the rows under the title and cannot centre anything on the
 * header. `sm` from the top and a 20px tall box match the header's content box
 * (`sm` of padding above a 20px line), so the control centres on the title's line whatever
 * the section's rows do.
 *
 * **`sm` from the end puts it under the context selector's caret** (Janne, PLT-6766), which sits
 * `md` inside a box that is itself inset by the rail's padding. The two glyph centres land within
 * a pixel of each other: the remainder is the selector box's own 1px border, which this control
 * has no equivalent of, and chasing it would cost an off-scale value for something no reader can
 * see (design/10-foundations.md, "Optical spacing").
 *
 * At the far end rather than beside the words, which is where `NavSectionTwistie` goes: the
 * twistie discloses the title and belongs to it, and this is a second control with a destination
 * of its own.
 */
export const NavSectionAction = styled.div`
  position: absolute;
  top: var(--space__sm);
  right: var(--space__sm);
  height: 20px;
  display: flex;
  align-items: center;
  /* Between two controls, as the Chats header's search and new-chat pair (PLT-7658). */
  gap: var(--space__xs);
  /* Over Primer's item content, which carries a z-index of 1. */
  z-index: 2;

  a,
  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    border: none;
    background-color: transparent;
    border-radius: var(--border-radius__full);
    color: var(--color__gray-600);
    cursor: pointer;

    /* The rail's own hover fill, so the action's pill and a row's are the same paint. It is a
       literal here for the reason design/40-shell-and-pages.md "Nav items" gives: Primer paints
       it and it is not a token. */
    &:hover {
      background-color: rgba(129, 139, 152, 0.1);
      color: var(--color__gray-800);
    }

    &:focus-visible {
      outline: var(--focus-ring__width) solid var(--focus-ring__color);
      outline-offset: var(--focus-ring__offset);
    }
  }
`;

// ============================================================================
// Section Empty Message
// ============================================================================

/**
 * The one plain sentence a titled section shows when it has no rows.
 *
 * Meta rather than a row: it is not navigable, so it takes `md` regular in `gray-600` rather than
 * the semibold `gray-900` a row's label takes (`DESIGN.md` rule 19), and it carries no icon, no
 * hover and no pill. It takes the row box's own inline padding, so it starts in the column the
 * section title and the rows' icons start in, and the rail keeps one straight left edge whether a
 * section has rows or not.
 */
export const NavSectionEmptyMessage = styled.li`
  list-style: none;
  margin: var(--space__sm) 0 0;
  padding: var(--space__sm) var(--space__lg);
  font-size: var(--font-size__md);
  font-weight: var(--font-weight__regular);
  color: var(--color__gray-600);
`;

// ============================================================================
// Sub Navigation Item Wrapper
// ============================================================================

export const SubNavItemWrapper = styled.div`
  margin-left: 0;
  font-weight: var(--font-weight__medium);
  width: 100%;

  li {
    width: 100%;
    font-size: var(--font-size__md);
    margin-left: 0;
    background-color: transparent;
    /* At rest as well as on hover, for the focus ring Primer aims at this box. See the matching
       rule in TopLevelItemWrap. */
    border-radius: var(--border-radius__full);

    :after {
      display: none;
    }

    a {
      display: flex;
      gap: var(--space__xs);
      ${navRowBox}
    }

    /* Disabled items render as Primer's outer <button> wrapping our DisabledNavButton.
       Match the enabled <a>'s box so disabled rows are the same height and their labels
       start in the same column. */
    > button {
      ${navRowBox}
    }

    /* Primer indents a row inside a NavList.SubNav with a Spacer element. The rail does not want
       it: every row takes the shared navRowBox, and so does a section's header, so one straight
       column runs down the whole rail with the uppercase titles starting where the rows' icons do.
       Left in, the rows under a title step 19px further in and the header no longer lines up with
       anything (PLT-6226). Important because Primer sets display: block on it. */
    .prc-ActionList-Spacer-dydlX {
      display: none !important;
    }
  }

  a {
    font-size: var(--font-size__md);
  }

  & + & {
    margin-top: var(--space__xs);
  }

  :first-child {
    margin-top: var(--space__sm);
  }
`;

// ============================================================================
// Sub Navigation Item Title
// ============================================================================

/* One line, ellipsised: a rail row's label may be user-authored (a conversation title, a portfolio
   name), and a label that wraps makes its row taller than its neighbours. The full label travels
   in the element's title, which is the condition the design system puts on any ellipsis
   (design/50-components.md "Truncation"). `min-width: 0` lets the block shrink below its
   intrinsic width inside Primer's label column; without it the ellipsis never engages. */
export const SubNavItemTitle = styled.div`
  font-weight: var(--font-weight__medium);
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/* PLT-8043 — the Coming soon badge's words. From md to lg Primer's pane makes the rail 256px, and
   beside "Coming soon" the label keeps 62px, which cuts "Upstream map" to "Upstrea…"; there the badge
   says "Soon". At every other width the label has room beside the full words. The full words stay
   the row's accessible name throughout: on the narrow rail only their box is hidden, as
   `VisuallyHidden` hides one, and the short word is hidden from assistive technology. */
const NARROW_RAIL = `(min-width: ${breakpoints[1]}) and (max-width: calc(${breakpoints[2]} - 0.02px))`;

const ComingSoonWords = styled.span`
  @media ${NARROW_RAIL} {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
  }
`;

const SoonWord = styled.span`
  display: none;

  @media ${NARROW_RAIL} {
    display: inline;
  }
`;

// ============================================================================
// Sub Navigation Item Component
// ============================================================================

interface SubNavItemProps {
  to: string;
  children: string;
  ariaCurrent?: 'page';
  icon: React.ComponentType<{ width?: number; height?: number }>;
  id?: string;
  disabled?: boolean;
  tooltipText?: string;
  /**
   * Query params to add to the row's destination (PLT-6529). `to` is a **pathname**:
   * `LinkWithSearch` builds `{ pathname: to, search }` and carries the current page's params
   * over, so a query string written into `to` would be URL-encoded into the path instead. A row
   * that identifies something by id — the unified investor rail's conversation rows — passes it
   * here.
   */
  additionalParams?: Record<string, string | null | undefined>;
  /**
   * Side effect to run on the row's own click, before the router navigates (PLT-6529).
   *
   * For the rows whose destination alone doesn't say what they do: the investor rail's
   * `New chat` has to reset the shared chat runtime, because `/chat` continues whatever thread
   * is loaded rather than starting a fresh one.
   *
   * Deliberately unguarded. A caller whose effect must not run on a cmd-click — open-in-new-tab
   * leaves this tab's state alone — checks the modifier keys itself, since whether that matters
   * is a property of the effect and not of the row. Neither this nor `additionalParams` applies
   * to a disabled row: that branch renders a button with no destination.
   */
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  trailingAction?: React.ReactNode;
  /**
   * A destination that is not open yet (PLT-8043): the row carries a neutral "Coming soon" badge at
   * its trailing edge, "Soon" on the 256px rail, and stays a real link, because the page it opens
   * says so (design/40-shell-and-pages.md "Sidebar"). Not `disabled`: that row's lock reads as "not
   * in your plan", which a page that is still being built is not.
   */
  isComingSoon?: boolean;
}

/**
 * Shared sub-navigation item with optional disabled/locked state.
 * When `disabled` is true, renders a non-clickable row with a lock icon and optional tooltip.
 * The disabled row is still a `NavList.Item`, so it stays a list item of the rail's `<ul>` and
 * keeps its place in Primer's keyboard navigation.
 */
export const SubNavItem = ({
  to,
  children,
  ariaCurrent,
  icon: Icon,
  id,
  disabled,
  tooltipText,
  additionalParams,
  onClick,
  trailingAction,
  isComingSoon,
}: SubNavItemProps) => {
  if (disabled) {
    const content = (
      <DisabledNavButton>
        <Icon width={20} height={20} />
        <span>{children}</span>
        <LockClosed width={20} height={20} />
      </DisabledNavButton>
    );

    return (
      <SubNavItemWrapper>
        <NavList.Item id={id}>
          {tooltipText ? (
            <Tooltip text={tooltipText} direction="e">
              {content}
            </Tooltip>
          ) : (
            content
          )}
        </NavList.Item>
      </SubNavItemWrapper>
    );
  }

  return (
    <SubNavItemWrapper>
      <NavList.Item
        as={LinkWithSearch}
        to={to}
        additionalParams={additionalParams}
        aria-current={ariaCurrent}
        id={id}
        onClick={onClick}
      >
        <NavList.LeadingVisual>
          <Icon width={20} height={20} />
        </NavList.LeadingVisual>
        <SubNavItemTitle title={children}>{children}</SubNavItemTitle>
        {isComingSoon === true && (
          <NavList.TrailingVisual>
            <Badge tone="neutral">
              <ComingSoonWords>Coming soon</ComingSoonWords>
              <SoonWord aria-hidden="true">Soon</SoonWord>
            </Badge>
          </NavList.TrailingVisual>
        )}
        {trailingAction}
      </NavList.Item>
    </SubNavItemWrapper>
  );
};

// ============================================================================
// Disabled Navigation Button (for locked features)
// ============================================================================

/* The row's own padding comes from the wrapper's `li > button` rule above — Primer's item button
   is the box — so this inner button carries none, and its gap is the one Primer's leading visual
   leaves before a label. */
export const DisabledNavButton = styled.button`
  width: 100%;
  background: transparent;
  border: none;
  padding: 0;
  cursor: default;
  opacity: 0.6;
  display: flex;
  align-items: center;
  gap: var(--space__md);
  border-radius: var(--border-radius__full);
  font-size: var(--font-size__md);
  font-weight: var(--font-weight__medium);
  color: inherit;
  box-sizing: border-box;
  flex: 1;
  white-space: nowrap;

  &:hover {
    background-color: transparent;
  }

  &:focus {
    outline: none;
  }

  span {
    flex: 1;
    text-align: left;
    white-space: nowrap;
  }
`;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Determines if a navigation item should be marked as active
 * @param path - The path to check
 * @param currentPath - The current pathname
 * @returns 'page' if active, undefined otherwise
 */
export const getAriaCurrent = (path: string, currentPath: string): 'page' | undefined =>
  currentPath.includes(path) ? 'page' : undefined;
